import { useMemo } from 'react';
import type { NameParts } from './nameOrder';

interface EmailedNameParts extends NameParts {
    workEmailAddress?: string | null;
}

const nameKey = (option: unknown): string | null => {
    if (!option || typeof option !== 'object') {
        return null;
    }
    const { firstName, lastName } = option as EmailedNameParts;
    return firstName && lastName
        ? JSON.stringify([firstName.trim().toLocaleLowerCase(), lastName.trim().toLocaleLowerCase()])
        : null;
};

/**
 * Returns the work email of an option whose first and last name another option of `options` shares, else
 * `null`, so a picker can tell same-name colleagues apart without widening every row.
 */
export const sameNameEmailGetter = (options: readonly unknown[]): ((option: unknown) => string | null) => {
    const counts = new Map<string, number>();
    options.forEach((option) => {
        const key = nameKey(option);
        if (key) {
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }
    });
    return (option) => {
        const key = nameKey(option);
        if (!key || (counts.get(key) ?? 0) < 2) {
            return null;
        }
        return (option as EmailedNameParts).workEmailAddress || null;
    };
};

export const useSameNameEmail = (options: readonly unknown[]): ((option: unknown) => string | null) =>
    useMemo(() => sameNameEmailGetter(options), [options]);
